import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { InteractionStatus, EventMessage, EventType } from '@azure/msal-browser';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit, OnDestroy {
  public loginDisplay = false;
  public errorMessage = '';
  private readonly _destroying$ = new Subject<void>();

  constructor(
    private authService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    // Check login status when interaction completes
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.setLoginDisplay();
        this.checkAndNavigate();
      });
    
    // Listen for login success
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        const returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/harmonization-data';
        this.router.navigate([returnUrl]);
      });
  }
  
  login(): void {
    this.authService.loginRedirect();
  }
  
  loginPopup(): void {
    this.authService.loginPopup()
      .subscribe({
        next: (result) => {
          console.log("Login successful");
          this.authService.instance.setActiveAccount(result.account);
        },
        error: (error) => {
          console.error("Login failed", error);
          this.errorMessage = "Login failed. Please try again.";
        }
      });
  }
  
  setLoginDisplay(): void {
    this.loginDisplay = this.authService.instance.getAllAccounts().length > 0;
  }
  
  checkAndNavigate(): void {
    if (this.loginDisplay) {
      const returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/harmonization-data';
      this.router.navigate([returnUrl]);
    }
  }
  
  resetAuthState(): void {
    // For troubleshooting - clears local storage and redirects to login
    const storageKeys = Object.keys(window.localStorage);
    const msalKeys = storageKeys.filter(key => 
      key.includes('msal') || 
      key.includes('MSAL') || 
      key.includes('interaction')
    );
    
    for (const key of msalKeys) {
      localStorage.removeItem(key);
    }
    
    window.location.reload();
  }
  
  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }
}